// ============================================================================
// src/lib/appStoreOperationApi.ts — the client of public.app_store_apply_operation
// ----------------------------------------------------------------------------
// Migration 246 (R-H: ppp-layer-groups-v1 and ppp-missed-cleared-v1) moves the
// remaining in-scope client writes off whole-row app_store upserts onto ONE
// audited SECURITY DEFINER entrypoint with private immutable request receipts
// in public.app_store_operation_receipts:
//
//   * `layer_groups.save`      — CAS on expected.updated_at (timestamptz compare
//                                on the server, never text; C1), unchanged
//                                stored rows pass through while new/changed rows
//                                enforce domain checks (C2).
//   * `missed.clear_entries`   — server-side union under the key advisory lock
//   * `missed.clear_all`         so concurrent clears never overwrite one another;
//                                stored entries merge as-is (A2) while submitted
//                                entries validate against MISSED_CLEARED_ENTRY_RE.
//
// Transport posture matches src/lib/pigOperationsApi.ts:
//   * One requestId per logical action; transport timeouts/network errors retry
//     up to 3 times (8s race, 1s/2s backoff) with the SAME requestId so the
//     server receipt deduplicates.
//   * A database SQLSTATE refusal (22023, 42501, WCF01, ...) is an authoritative
//     answer and returns immediately without retry.
// ============================================================================

import type {SaveStatusSink} from './appStoreBlobApi.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import type {SupabaseClient} from './supabaseTypes.js';

export const LAYER_GROUPS_APP_STORE_KEY = 'ppp-layer-groups-v1' as const;
export const MISSED_CLEARED_APP_STORE_KEY = 'ppp-missed-cleared-v1' as const;

/**
 * The exact submitted-entry shape enforced by migration 246 on
 * `missed.clear_entries` and `missed.clear_all` (C3):
 *   - dailyTargetKey: `<row-id-or-prefix-slug>|<YYYY-MM-DD>`
 *   - equipment attention: `equip-overdue-<id>|<kind>|<hours>` and
 *     `equip-fillup-<id>|streak<n>|n<n>`
 */
export const MISSED_CLEARED_ENTRY_RE = /^[A-Za-z0-9_.-]{1,100}(\|[A-Za-z0-9_.-]{1,60}){1,3}$/;

export type AppStoreOperation = 'layer_groups.save' | 'missed.clear_entries' | 'missed.clear_all';

export type AppStoreOpRequest =
  | {
      requestId: string;
      operation: 'layer_groups.save';
      target: {key: typeof LAYER_GROUPS_APP_STORE_KEY};
      expected: {updated_at: string | null};
      change: {document: unknown[]};
    }
  | {
      requestId: string;
      operation: 'missed.clear_entries' | 'missed.clear_all';
      target: {key: typeof MISSED_CLEARED_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {entries: string[]};
    };

export type AppStoreOpOutcome =
  | {
      status: 'saved';
      written: true;
      revision: string | null;
      updatedAt: string | null;
      document: unknown[] | null;
      replayed: boolean;
      mustReread: boolean;
    }
  | {
      status: 'no_change';
      written: false;
    }
  | {
      status: 'conflict';
      reason: string;
      currentUpdatedAt: string | null;
    }
  | {
      status: 'rejected';
      code: string;
      message: string;
    }
  | {
      status: 'unknown';
      message: string;
      mustReread: true;
    };

export const LAYER_GROUPS_CONFLICT_MESSAGE =
  'Layer groups were updated elsewhere while you were editing. Reload and try again.';

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
const SQLSTATE = /^[0-9A-Z]{5}$/;

type RpcError = {code?: string; message?: string} | null;

/** A fresh request id for one logical app_store operation. */
export function newAppStoreRequestId(prefix = 'appstore'): string {
  return `${prefix}-${newClientSubmissionId()}`;
}

/** Interpret one reply from public.app_store_apply_operation. */
export function interpretAppStoreReply(request: AppStoreOpRequest, data: unknown): AppStoreOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
  }
  switch (d.kind) {
    case 'saved': {
      if (d.ok !== true) break;
      const replayed = d.replayed === true;
      const doc = !replayed && Array.isArray(d.document) ? (d.document as unknown[]) : null;
      return {
        status: 'saved',
        written: true,
        revision: typeof d.revision === 'string' ? d.revision : null,
        updatedAt: typeof d.updated_at === 'string' ? d.updated_at : typeof d.revision === 'string' ? d.revision : null,
        document: doc,
        replayed,
        mustReread: replayed || doc === null,
      };
    }
    case 'no_change':
      if (d.ok !== true) break;
      return {status: 'no_change', written: false};
    case 'conflict':
      return {
        status: 'conflict',
        reason: typeof d.reason === 'string' ? d.reason : 'stale_base',
        currentUpdatedAt: typeof d.current_updated_at === 'string' ? d.current_updated_at : null,
      };
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
}

/**
 * Execute one `public.app_store_apply_operation` RPC call.
 * Never throws. Retries transport timeouts/errors with the SAME requestId;
 * returns `rejected` immediately on a 5-character SQLSTATE.
 */
export async function applyAppStoreOperation(
  sb: SupabaseClient,
  request: AppStoreOpRequest,
): Promise<AppStoreOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('app_store_apply_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change,
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        throw new Error(error.message || 'transport');
      }
      return interpretAppStoreReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('app_store_apply_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.', mustReread: true};
}

/** Map a SQLSTATE rejection to a user-facing message. */
export function appStoreRejectionMessage(outcome: Extract<AppStoreOpOutcome, {status: 'rejected'}>): string {
  switch (outcome.code) {
    case '22023':
      return outcome.message.replace(/^app_store_apply_operation:\s*/, '');
    case '42501':
      return 'Your session or role changed. Sign in again; your change is kept.';
    case 'WCF01':
      return 'This change no longer matches what was saved. Reload and try again.';
    default:
      return outcome.message || 'The change was refused.';
  }
}

/**
 * Fire-and-forget single-entry clear for HomeDashboard (`missed.clear_entries`).
 * Preserves B3.3's optimistic-first, no-header-indicator posture while moving
 * the persistence onto the server-side union under the key lock.
 */
export function clearMissedEntryOnServer(sb: SupabaseClient, key: string): void {
  if (!key) return;
  const requestId = newAppStoreRequestId('missed-entry');
  void applyAppStoreOperation(sb, {
    requestId,
    operation: 'missed.clear_entries',
    target: {key: MISSED_CLEARED_APP_STORE_KEY},
    expected: {},
    change: {entries: [key]},
  });
}

/**
 * Fire-and-forget clear-all for HomeDashboard (`missed.clear_all`).
 * Submits the entries the caller computed; the server unions them under the
 * key lock so concurrent clears from other sessions are never lost.
 */
export function clearAllMissedOnServer(sb: SupabaseClient, keys: readonly string[]): void {
  const entries = keys.filter((k) => typeof k === 'string' && k.length > 0);
  if (entries.length === 0) return;
  const requestId = newAppStoreRequestId('missed-all');
  void applyAppStoreOperation(sb, {
    requestId,
    operation: 'missed.clear_all',
    target: {key: MISSED_CLEARED_APP_STORE_KEY},
    expected: {},
    change: {entries},
  });
}

/**
 * Save `ppp-layer-groups-v1` through `layer_groups.save` with expected
 * `updated_at` CAS and optional `onPersisted` callback before `saved` status.
 */
export async function saveLayerGroupsDocument(
  sb: SupabaseClient,
  params: {
    expectedUpdatedAt: string | null;
    document: unknown[];
    requestId?: string;
    onPersisted?: () => void;
  },
): Promise<AppStoreOpOutcome> {
  const requestId = params.requestId || newAppStoreRequestId('lg');
  const outcome = await applyAppStoreOperation(sb, {
    requestId,
    operation: 'layer_groups.save',
    target: {key: LAYER_GROUPS_APP_STORE_KEY},
    expected: {updated_at: params.expectedUpdatedAt},
    change: {document: params.document},
  });
  if (outcome.status === 'saved' || outcome.status === 'no_change') {
    try {
      params.onPersisted?.();
    } catch (refreshError) {
      console.warn('Saved layer groups document; refresh callback failed:', refreshError);
    }
  }
  return outcome;
}

/** Report save status transition (`saved` or `error`) with the standard auto-clear timers. */
export function reportAppStoreSaveStatus(onSaveStatus: SaveStatusSink, status: 'saving' | 'saved' | 'error'): void {
  onSaveStatus(status);
  if (status === 'saved') {
    setTimeout(() => onSaveStatus(''), 2500);
  } else if (status === 'error') {
    setTimeout(() => onSaveStatus(''), 4000);
  }
}
